// i18n
import {
  changeLanguage,
  defaultLocale,
  translateInComponent,
} from 'infras/i18n';
import translations from 'presentations/views/components/pages/apps/CalendarYearPage/i18n.json';

import { imagePath } from 'presentations/assets';
import CalendarMonthPage from 'presentations/views/components/pages/apps/CalendarMonthPage';
import { monthlyCalendar } from 'usecases/app_calendar';
import { gengoList, Gengo } from 'usecases/app_calendar/jp/gengo';
import { formatGengoYear } from 'usecases/app_calendar/jp/gengo/formatter';
import {
  holidayList,
  holidayMasterList,
} from 'usecases/app_calendar/jp/holidays';
import { setBreadcrumbs } from 'presentations/views/helpers/breadcrumbs';
import MainDocument from 'presentations/views/components/MainDocument';
import {
  appCalendarMonthPath,
  appCalendarPath,
  appCalendarYearPath,
  webPath,
} from 'presentations/routes/path';
import {
  getLogo,
  getBreadcrumbs,
  getFrequentlyAskedQuestions,
} from 'presentations/views/components/atoms/StructuredData';
import { getDateTime } from 'infras/datetime';
import i18next from 'i18next';
import { GetStaticPropsContext } from 'next';

changeLanguage('ja');
i18next.init({
  interpolation: {
    format: function (value, format) {
      if (format === 'uppercase') {
        return value.toUpperCase();
      }
      if (value instanceof Date && format) {
        return getDateTime(value, 'Asia/Tokyo').toFormat(format);
      }
      return value;
    },
  },
});

interface ServerProps {
  locale: string;
  calendars: any[];
  linkCanonical: string;
  gengo: Gengo;
  holidays: any;
  holidayMasters: any;
  year: number;
  month: number;
}

export async function getStaticPaths({ locales }: { locales: string[] }) {
  if (process.env['BUILD_ENV'] !== 'production') {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
  const paths: Object[] = [];
  ['ja'].forEach((locale) => {
    const startYear = 2023;
    const endYear = 2030;
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 1; month <= 12; month++) {
        paths.push({
          params: { year: String(year), month: String(month) },
          locale,
        });
      }
    }
  });
  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params,
  locale,
}: GetStaticPropsContext) {
  if (!params) return { notFound: true };
  if (locale !== defaultLocale) return { notFound: true };

  const year = Number(params.year);
  if (Number.isNaN(year)) return { notFound: true };
  const month = Number(params.month);
  if (Number.isNaN(month)) return { notFound: true };
  const months = [];
  const holidays = holidayList(year);
  const holidayMasters = holidayMasterList();
  const gengo = gengoList(year)[0];
  if (!gengo) {
    return { notFound: true };
  }

  for (let i = 1; i <= 12; i++) months.push(i);
  const calendars = months.map((month) => {
    return monthlyCalendar(year, month, { locale, holidays });
  });

  const path = appCalendarMonthPath(year, month);
  const linkCanonical = webPath(path);

  const props: ServerProps = {
    locale,
    linkCanonical,
    calendars,
    gengo,
    holidays,
    holidayMasters,
    year,
    month,
  };
  return { props };
}

const Page = (props: ServerProps) => {
  const t = translateInComponent(translations);

  const date = new Date(props.year, props.month - 1, 1);

  const calendar = t('calendar');
  const breadcrumbs = [];
  breadcrumbs.push({
    link: appCalendarPath(),
    title: t('calendar'),
  });
  breadcrumbs.push({
    link: appCalendarYearPath(props.year),
    title: t('date.year', { date }),
  });
  breadcrumbs.push({
    link: appCalendarMonthPath(props.year, props.month),
    title: t('date.month', { date }),
  });

  let gengo = t('gengoTitle', {
    year: formatGengoYear(props.year - props.gengo.start + 1),
    name: props.gengo.name,
  });
  const title = t('title', {
    date,
    gengo: gengo,
    calendar,
  });
  gengo = t('gengoTitleKana', {
    year: formatGengoYear(props.year - props.gengo.start + 1),
    name: props.gengo.name,
    kana: props.gengo.kana,
  });
  const description = t('description', {
    date,
    gengo: gengo,
    calendar,
  });

  const faqs: any[] = [];
  if (props.holidays) {
    for (let m = 1; m <= 12; m++) {
      const date = new Date(props.year, m - 1, 1);
      const days = [];
      for (const [ymdText, text] of Object.entries(props.holidays)) {
        const ymd = String(ymdText).split('-');
        if (props.year === Number(ymd[0]) && m === Number(ymd[1])) {
          days.push(
            `${t('holidayDate', {
              date: new Date(ymdText),
            })}${text}`,
          );
        }
      }
      const faq = {
        question: t('faqQuestion', { date }),
        answer: '',
      };
      if (days.length > 0) {
        faq.answer = t('faqAnswer', {
          text: days.join(','),
          length: days.length,
        });
      } else {
        faq.answer = t('faqAnswerNone');
      }
      faqs.push(faq);
    }
  }

  const routes = setBreadcrumbs(breadcrumbs);
  return (
    <MainDocument
      title={title}
      description={description}
      locales={['ja']}
      isShowDownloadLink
      linkCanonical={props.linkCanonical}
      disableGoogleTagManager={true}
      url={props.linkCanonical}
      image={imagePath(`apps/calendar/mv/${props.month}.jpg`)}
      structuredData={
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: getLogo() }}
          />
          {breadcrumbs.length > 0 && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: getBreadcrumbs({ items: breadcrumbs }),
              }}
            />
          )}
          {faqs.length > 0 && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: getFrequentlyAskedQuestions({ items: faqs }),
              }}
            />
          )}
        </>
      }
      isUseAdsense
    >
      <CalendarMonthPage
        mainCalendar={props.calendars[props.month - 1]}
        calendarName={calendar}
        calendars={props.calendars}
        gengo={props.gengo}
        holidays={props.holidays}
        holidayMasters={props.holidayMasters}
        year={props.year}
        month={props.month}
        routes={routes}
      />
    </MainDocument>
  );
};

export default Page;
